/**
 * ChatFilesDisplay
 *
 * Shows draft and chat attachments around the composer.
 *
 * Primary responsibilities:
 * - Render pending message images and files
 * - Open attachment previews
 * - Remove draft attachments before send
 *
 * Dependencies:
 * - Chat attachment context
 * - Storage URL helpers
 */

import { FC, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Image from 'next/image'
import { XIcon } from 'lucide-react'
import { toast } from 'sonner'
import {
  IconFileFilled,
  IconFileTypeCsv,
  IconFileTypeDocx,
  IconFileTypePdf,
  IconFileTypeTxt,
  IconJson,
  IconLoader2,
  IconMarkdown,
  IconMusic,
  IconVolume,
  IconVolumeOff
} from '@tabler/icons-react'
import { ChatFile, MessageImage } from '@/types'
import { guessFileExtensionByContentType } from '@/lib/content-type'
import { getFileFromStorage } from '@/db/storage/files'
import { getFileUrl } from '@/lib/utils/get-file-url'
import { clearTryTempFileContent } from '@/lib/chat/try-temp-attachments'
import useHotkey from '@/lib/hooks/use-hotkey'

import { ChatbotUIContext } from '@/context/context'
import { useScopedAttachmentKey } from '@/components/chat/chat-hooks/use-scoped-user-input'

import { Button } from '../ui/button'
import { FilePreview } from '../ui/file-preview'
import { ImageModal } from '../ui/image-modal'
import { ChatRetrievalSettings } from './chat-retrieval-settings'
import { WithTooltip } from '../ui/with-tooltip'
import { IconCircleFilled } from '@tabler/icons-react'
import { cn } from '@/lib/utils'

interface ChatFilesDisplayProps {
  chatId?: string
}

const isLoadingChatFile = (file: ChatFile) => file.id.startsWith('loading')

export const ChatFilesDisplay: FC<ChatFilesDisplayProps> = ({ chatId }) => {
  const {
    files,
    newMessageImages: contextNewMessageImages,
    setNewMessageImages: setContextNewMessageImages,
    newMessageFiles: contextNewMessageFiles,
    setNewMessageFiles: setContextNewMessageFiles,
    setShowFilesDisplay: setContextShowFilesDisplay,
    showFilesDisplay: contextShowFilesDisplay,
    chatFiles: contextChatFiles,
    chatImages: contextChatImages,
    setChatImages: setContextChatImages,
    setChatFiles: setContextChatFiles,
    activeAttachmentKey,
    setUseRetrieval,
    getAttachmentStoreKey,
    getScopedAttachmentBindings
  } = useContext(ChatbotUIContext)
  const scopedAttachmentKey = useScopedAttachmentKey({
    activeAttachmentKey,
    chatId,
    getAttachmentStoreKey
  })
  const scopedAttachmentBindings =
    getScopedAttachmentBindings(scopedAttachmentKey)
  const scopedAttachmentState = scopedAttachmentBindings.getSnapshot()
  const newMessageImages =
    scopedAttachmentState?.newMessageImages ?? contextNewMessageImages
  const setNewMessageImages =
    scopedAttachmentBindings?.setNewMessageImages ?? setContextNewMessageImages
  const newMessageFiles =
    scopedAttachmentState?.newMessageFiles ?? contextNewMessageFiles
  const setNewMessageFiles =
    scopedAttachmentBindings?.setNewMessageFiles ?? setContextNewMessageFiles
  const showFilesDisplay =
    scopedAttachmentState?.showFilesDisplay ?? contextShowFilesDisplay
  const setShowFilesDisplay =
    scopedAttachmentBindings?.setShowFilesDisplay ?? setContextShowFilesDisplay
  const chatFiles = scopedAttachmentState?.chatFiles ?? contextChatFiles
  const chatImages = scopedAttachmentState?.chatImages ?? contextChatImages
  const setChatImages = scopedAttachmentBindings?.setChatImages ?? setContextChatImages
  const setChatFiles = scopedAttachmentBindings?.setChatFiles ?? setContextChatFiles

  useHotkey('f', () => setShowFilesDisplay((prev: boolean) => !prev))
  useHotkey('e', () => setUseRetrieval((prev: boolean) => !prev))

  const [selectedFile, setSelectedFile] = useState<ChatFile | null>(null)
  const [selectedImage, setSelectedImage] = useState<MessageImage | null>(null)
  const [showPreview, setShowPreview] = useState(false)

  const messageImages = [
    ...newMessageImages.filter(
      (image: MessageImage) =>
        !chatImages.some((chatImage: MessageImage) => chatImage.messageId === image.messageId)
    )
  ]

  const combinedChatFiles = [
    ...newMessageFiles.filter(
      (file: ChatFile) => !chatFiles.some((chatFile: ChatFile) => chatFile.id === file.id)
    ),
    ...chatFiles
  ]

  const combinedMessageFiles = [...messageImages, ...combinedChatFiles]

  const getLinkAndView = async (file: ChatFile) => {
    if (isLoadingChatFile(file)) return

    // Draft composer attachments still have the local File — open that first so
    // a just-uploaded PDF doesn't toast "Failed to open file" while storage
    // path resolution is still catching up.
    if (file.file) {
      const objectUrl = URL.createObjectURL(file.file)
      window.open(objectUrl, '_blank', 'noopener,noreferrer')
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
      return
    }

    const fileRecord = files.find((candidate) => candidate.id === file.id)

    if (!fileRecord?.file_path?.trim()) {
      toast.error('File is still preparing. Try again in a moment.')
      return
    }

    try {
      const link = await getFileUrl(fileRecord.file_path)
      window.open(link, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Error opening chat file:', error)
      toast.error("Couldn't open that file. Try again.")
    }
  }

  return showFilesDisplay && combinedMessageFiles.length > 0 ? (
    <div className="flex min-w-0 flex-col flex-wrap justify-center gap-2 pb-1">
      {showPreview && selectedImage && (
        <ImageModal
          isOpen={showPreview}
          src={selectedImage.base64 || selectedImage.url || selectedImage.path}
          alt="File image"
          onClose={() => {
            setShowPreview(false)
            setSelectedImage(null)
          }}
        />
      )}

      {showPreview && selectedFile && (
        <FilePreview
          type="file"
          item={selectedFile}
          isOpen={showPreview}
          onOpenChange={(isOpen: boolean) => {
            setShowPreview(isOpen)
            setSelectedFile(null)
          }}
        />
      )}

      <div className="min-w-0 space-y-2">
        {/*<div className="flex w-full items-center justify-center">*/}
        {/*  <Button*/}
        {/*    className="flex h-[32px] w-[140px] space-x-2"*/}
        {/*    onClick={() => setShowFilesDisplay(false)}*/}
        {/*  >*/}
        {/*    <RetrievalToggle />*/}

        {/*    <div>Hide files</div>*/}

        {/*    <div onClick={e => e.stopPropagation()}>*/}
        {/*      <ChatRetrievalSettings />*/}
        {/*    </div>*/}
        {/*  </Button>*/}
        {/*</div>*/}

        <div className="flex w-full min-w-0 max-w-full">
          <div className="flex w-full min-w-0 flex-wrap gap-2 pt-2">
            {messageImages.map((image, index) => (
              <div
                key={index}
                className="hover:bg-accent relative flex h-[64px] min-w-0 items-center rounded-xl transition-[background-color,opacity,transform] duration-200 ease-out"
              >
                <button
                  type="button"
                  aria-label="Preview uploaded image"
                  className="focus-visible:ring-ring inline-flex size-16 items-center justify-center rounded-xl focus-visible:outline-none focus-visible:ring-2"
                  onClick={() => {
                    setSelectedImage(image)
                    setShowPreview(true)
                  }}
                >
                  <Image
                    className="rounded"
                    // Force the image to be 56px by 56px
                    style={{
                      minWidth: '56px',
                      minHeight: '56px',
                      maxHeight: '56px',
                      maxWidth: '56px'
                    }}
                    src={image.base64} // Preview images will always be base64
                    alt="File image"
                    width={56}
                    height={56}
                  />
                </button>

                <button
                  type="button"
                  aria-label="Remove uploaded image"
                  className="bg-background text-muted-foreground ring-border hover:bg-muted hover:text-foreground focus-visible:ring-ring absolute -right-2 -top-2 inline-flex size-8 items-center justify-center rounded-full shadow-sm ring-1 transition-colors focus-visible:outline-none focus-visible:ring-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    setNewMessageImages(
                      newMessageImages.filter((f: MessageImage) => f.url !== image.url)
                    )
                    setChatImages(chatImages.filter((f: MessageImage) => f.url !== image.url))
                  }}
                >
                  <XIcon className="size-4" aria-hidden="true" />
                </button>
              </div>
            ))}

            {combinedChatFiles.map((file, index) =>
              isLoadingChatFile(file) ? (
                <div
                  key={index}
                  className="relative flex h-[64px] min-w-0 max-w-full items-center gap-3 rounded-xl border px-4 py-3"
                >
                  <div className="rounded bg-violet-500 p-2">
                    <IconLoader2 className="animate-spin" />
                  </div>

                  <div className="truncate text-sm">
                    <div className="truncate">{file.name}</div>
                    <div className="truncate opacity-50">
                      {guessFileExtensionByContentType(file.type)}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  key={file.id}
                  className="relative flex h-[64px] min-w-0 max-w-full items-center rounded-xl"
                >
                  <button
                    type="button"
                    aria-label={`Open ${file.name}`}
                    className="bg-background/95 hover:bg-accent focus-visible:ring-ring flex h-[64px] min-w-0 max-w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2"
                    onClick={() => getLinkAndView(file)}
                  >
                    <div className="shrink-0 rounded bg-violet-500 p-2">
                      {(() => {
                        let fileExtension = guessFileExtensionByContentType(file.type)

                        switch (fileExtension) {
                          case 'pdf':
                            return <IconFileTypePdf stroke={1.5} />
                          case 'markdown':
                            return <IconMarkdown stroke={1.5} />
                          case 'txt':
                            return <IconFileTypeTxt stroke={1.5} />
                          case 'json':
                            return <IconJson stroke={1.5} />
                          case 'csv':
                            return <IconFileTypeCsv stroke={1.5} />
                          case 'docx':
                            return <IconFileTypeDocx stroke={1.5} />
                          case 'mp3':
                          case 'wav':
                          case 'm4a':
                          case 'aac':
                          case 'flac':
                          case 'ogg':
                          case 'webm':
                          case 'audio':
                            return <IconMusic stroke={1.5} />
                          default:
                            return <IconFileFilled stroke={1.5} />
                        }
                      })()}
                    </div>

                    <div className="min-w-0 truncate text-sm">
                      <div className="truncate">{file.name}</div>
                      <div className="truncate opacity-50">
                        {guessFileExtensionByContentType(file.type)}
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    className="bg-background text-muted-foreground ring-border hover:bg-muted hover:text-foreground focus-visible:ring-ring absolute -right-2 -top-2 inline-flex size-8 items-center justify-center rounded-full shadow-sm ring-1 transition-colors focus-visible:outline-none focus-visible:ring-2"
                    onClick={(e) => {
                      e.stopPropagation()
                      clearTryTempFileContent(file.id)
                      setNewMessageFiles(newMessageFiles.filter((f: ChatFile) => f.id !== file.id))
                      setChatFiles(chatFiles.filter((f: ChatFile) => f.id !== file.id))
                    }}
                  >
                    <XIcon className="size-4" aria-hidden="true" />
                  </button>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  ) : (
    combinedMessageFiles.length > 0 && (
      <div className="flex flex-col flex-wrap justify-center gap-2 pb-2">
        <div className="flex w-full items-center justify-center space-x-2">
          <Button
            className="flex h-[32px] w-[140px] space-x-2"
            onClick={() => setShowFilesDisplay(true)}
          >
            <RetrievalToggle />

            <div>
              {' '}
              View {combinedMessageFiles.length} file
              {combinedMessageFiles.length > 1 ? 's' : ''}
            </div>

            <div onClick={(e) => e.stopPropagation()}>
              <ChatRetrievalSettings />
            </div>
          </Button>
        </div>
      </div>
    )
  )
}

const RetrievalToggle = ({}) => {
  const { useRetrieval, setUseRetrieval } = useContext(ChatbotUIContext)

  return (
    <div className="flex items-center">
      <WithTooltip
        delayDuration={0}
        side="top"
        display={
          <div>
            {useRetrieval
              ? 'File retrieval is enabled on the selected files for this message. Click the indicator to disable.'
              : 'Click the indicator to enable file retrieval for this message.'}
          </div>
        }
        trigger={
          <IconCircleFilled
            className={cn(
              'p-1',
              useRetrieval ? 'text-green-500' : 'text-red-500',
              useRetrieval ? 'hover:text-green-200' : 'hover:text-red-200'
            )}
            size={24}
            stroke={1.5}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              setUseRetrieval((prev: boolean) => !prev)
            }}
          />
        }
      />
    </div>
  )
}
